import { useState } from 'react';
import { useArchitectureStore } from '../store/useArchitectureStore';
import { useArchitecturePrompt } from './useArchitecturePrompt';
import { callGeminiImageEditor, callGeminiPlanToElevation } from '../services/geminiService';
import { useAuth } from '../context/AuthContext';

export const useArchitectureGenerator = () => {
    const {
        sourceImage: globalSource,
        setResultImage: setGlobalResult,
        setIsProcessing,
        activeTask,
        updateTaskData,
        completeTask,
        setLastError
    } = useArchitectureStore();
    const { generatePrompt, generateNegativePrompt, activeGuideline } = useArchitecturePrompt(); // Access activeGuideline
    const { profile } = useAuth();

    const generate = async () => {
        setIsProcessing(true);

        try {
            // --- 1. PLAN TO ELEVATION WORKFLOW ---
            if (activeTask?.type === 'PLAN_TO_ELEVATION') {
                const { groundFloor, typicalFloor, groundFloorHeight, typicalFloorHeight, floorsCount } = activeTask.data;

                if (!groundFloor) throw new Error("Ground floor plan is missing");

                const result = await callGeminiPlanToElevation(
                    groundFloor,
                    typicalFloor,
                    {
                        groundHeight: Number(groundFloorHeight) || 4,
                        typicalHeight: Number(typicalFloorHeight) || 3.5,
                        floors: Number(floorsCount) || 2
                    },
                    generatePrompt("Apartment Building"), // Use current style settings
                    profile?.id || 'guest'
                );

                updateTaskData({ resultImage: result });
                completeTask(); // Mark task as done
                return;
            }

            // --- 2. STYLE TRANSFORMER & INPAINTING (Standard) ---

            // Determine Source: Task Data > Global State
            const sourceToUse = activeTask?.data?.sourceImage || globalSource;
            const pointsToUse = activeTask?.data?.maskPoints || null;

            if (!sourceToUse) {
                console.warn("No source image to transform");
                return;
            }

            // Clear previous result in appropriate location
            if (activeTask) updateTaskData({ resultImage: null });
            else setGlobalResult(null);

            // Generate Prompt
            const promptParts = generatePrompt("");
            let transformationInstruction = `${promptParts}
            
            CRITICAL TASK INSTRUCTIONS: 
            You must strictly maintain the main structural massing, overall width-to-height ratio, and exact window locations. 
            Completely replace the facade materials, texturing, and details to match the regional style. 
            Ignore the original color and texture, but strictly adhere to the existing geometry and fenestration pattern.`;

            if (pointsToUse && pointsToUse.length > 0) {
                transformationInstruction = `TASK: Redesign ONLY the selected area. ${promptParts}\n\nCRITICAL: Keep the surrounding context exactly as is.`;
            }

            // --- 3. PATTERN INJECTION (Multimodal) ---
            let patternImages: string[] = [];
            if (activeGuideline?.guideline_data?.pattern_images) {
                try {
                    const patterns = activeGuideline.guideline_data.pattern_images;
                    // Fetch all pattern images in parallel
                    const loadedPatterns = await Promise.all(patterns.map(async (path: string) => {
                        const response = await fetch(path);
                        const blob = await response.blob();
                        return new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result as string);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });
                    }));
                    patternImages = loadedPatterns;
                    console.log(`Loaded ${patternImages.length} pattern reference images for ${activeGuideline.name}`);
                } catch (err) {
                    console.error("Failed to load pattern images:", err);
                }
            }

            // Call Service
            const result = await callGeminiImageEditor(
                sourceToUse,
                pointsToUse,
                { width: 1024, height: 1024 },
                transformationInstruction,
                patternImages.length > 0 ? patternImages : null, // Pass array of patterns
                'modify',
                generateNegativePrompt(),
                95, // Increased AI Strength for stronger redesign
                40, // Reduced Resemblance to allow more style change
                80, // Clarity
                85, // Sharpness
                profile?.id || 'guest',
                'standalone'
            );

            // Save Result
            if (activeTask) {
                updateTaskData({ resultImage: result });
                // We might not want to complete the task immediately for style transformer to allow retries, 
                // but for now let's keep it manual or auto-complete? 
                // Let's NOT auto-complete style transfer so user can tweak.
            } else {
                setGlobalResult(result);
            }

        } catch (error: any) {
            console.error("Generation Failed:", error);
            setLastError(error.message || "An unexpected error occurred during generation.");
        } finally {
            setIsProcessing(false);
        }
    };

    return { generate };
};
